import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCloudTalkRealtime } from '../../hooks/useCloudTalkRealtime';
import { usePortal } from '../../contexts/PortalContext';
import { fetchCloudTalkLiveCalls, formatLiveCallDuration } from '../../lib/demCloudTalk';
import type { DemCloudTalkLiveCall } from '../../types/dem';

/**
 * DEM-wide sticky toasts for CloudTalk live calls (ringing / on call).
 * Mount once under DemAppShell so every DEM page can see incoming calls.
 */
export function CloudTalkLiveToastHost() {
  const navigate = useNavigate();
  const { basePath } = usePortal();
  const [calls, setCalls] = useState<DemCloudTalkLiveCall[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [nowMs, setNowMs] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    try {
      setCalls(await fetchCloudTalkLiveCalls());
    } catch {
      /* keep prior snapshot */
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useCloudTalkRealtime({
    onChange: () => {
      void refresh();
    },
  });

  useEffect(() => {
    if (calls.length === 0) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [calls.length]);

  // Forget dismissals once the call leaves the live table.
  useEffect(() => {
    const active = new Set(calls.map((c) => c.callUuid));
    setDismissed((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (active.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [calls]);

  const visible = calls.filter((c) => !dismissed.has(c.callUuid));
  if (visible.length === 0) return null;

  const cloudTalkPath = basePath ? `${basePath}/cloudtalk` : '/cloudtalk';

  return createPortal(
    <div className="dem-cloudtalk-live-toasts" aria-live="polite" aria-label="Live CloudTalk calls">
      {visible.map((call) => {
        const ringing = call.phase !== 'answered';
        const inbound =
          !call.direction || call.direction === 'incoming' || call.direction === 'inbound';
        return (
          <div
            key={call.callUuid}
            className={`dem-cloudtalk-live-toast dem-cloudtalk-live-toast--${
              ringing ? 'ringing' : 'answered'
            }`}
            role={ringing ? 'alert' : 'status'}
          >
            <button
              type="button"
              className="dem-cloudtalk-live-toast__main"
              onClick={() => navigate(cloudTalkPath)}
            >
              <div className="dem-cloudtalk-live-toast__top">
                <span
                  className={`dem-cloudtalk-live-toast__phase dem-cloudtalk-live-toast__phase--${
                    ringing ? 'ringing' : 'answered'
                  }`}
                >
                  <span className="dem-cloudtalk-live-toast__pulse" aria-hidden />
                  {ringing ? (inbound ? 'Incoming call' : 'Outbound ringing') : 'On call'}
                </span>
                <span className="dem-cloudtalk-live-toast__duration">
                  {formatLiveCallDuration(call.startedAt, nowMs)}
                </span>
              </div>
              <p className="dem-cloudtalk-live-toast__contact" title={call.contactName}>
                {call.contactName}
              </p>
              <p className="dem-cloudtalk-live-toast__number">{call.externalNumber}</p>
              <p className="dem-cloudtalk-live-toast__meta">
                {[call.calledLine, call.teamGroup, call.agentName || null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </button>
            <button
              type="button"
              className="dem-cloudtalk-live-toast__close"
              aria-label="Dismiss live call toast"
              onClick={() =>
                setDismissed((prev) => {
                  const next = new Set(prev);
                  next.add(call.callUuid);
                  return next;
                })
              }
            >
              ×
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
